import React, { useContext, useEffect, useState, SyntheticEvent } from 'react'
import { Box, Divider, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import FixedWidthContainer from 'components/FixedWidthContainer'
import { CartContext } from 'contexts/CartContext'
import {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteChangeDetails
} from '@mui/material/Autocomplete'
import useSearchProducts from 'hooks/useSearchProducts'
import { Product } from 'hooks/useProduct'
import SectionHeader from 'components/SectionHeader'
import QuickOrderList from 'pages/QuickOrder/QuickOrderList'
import QuickOrderSearchBar from 'pages/QuickOrder/QuickOrderSearchBar'

const QuickOrderPage = () => {
  const { t } = useTranslation()
  const [searchKeyword, setSearchKeyword] = useState<string>('')
  const [searchResults, setSearchResults] = useState<Product[]>([])
  const [quantities, setQuantities] = useState<{ [key: string]: number }>({})
  const [isAssembled, setIsAssembled] = useState<{ [key: string]: boolean }>({})
  const [hinges, setHinges] = useState<{ [key: string]: string }>({})

  const { updateCartItem, cart } = useContext(CartContext)

  const { products: suggestions } = useSearchProducts({
    keyword: searchKeyword,
    limit: 5
  })

  const addProductToResults = (product: Product) => {
    setSearchResults(prev => {
      const exists = prev.some(item => item.id === product.id)
      if (!exists) {
        const updatedResults = [product, ...prev]
        localStorage.setItem('searchResults', JSON.stringify(updatedResults))

        setQuantities(prevQuantities => {
          const updatedQuantities = { ...prevQuantities, [product.id]: 1 }
          localStorage.setItem('quantities', JSON.stringify(updatedQuantities))
          return updatedQuantities
        })

        setIsAssembled(prevAssembled => ({
          ...prevAssembled,
          [product.id]: false
        }))
        setHinges(prevHinges => ({
          ...prevHinges,
          [product.id]: ''
        }))
        return updatedResults
      }
      return prev
    })
  }

  const handleSearchChange = (
    event: SyntheticEvent<Element, Event>,
    value: string,
    _reason: AutocompleteInputChangeReason
  ) => {
    setSearchKeyword(value)
  }

  const handleSuggestionSelect = (
    event: SyntheticEvent<Element, Event>,
    product: Product | null,
    _reason: AutocompleteChangeReason,
    _details?: AutocompleteChangeDetails<Product>
  ) => {
    if (product) {
      addProductToResults(product)
      setSearchKeyword('')
    }
  }

  const handleQuantityChange = (productId: string, quantity: number) => {
    const newQuantities = { ...quantities, [productId]: quantity }
    setQuantities(newQuantities)
    localStorage.setItem('quantities', JSON.stringify(newQuantities))

    if (cart?.items?.some) {
      const isProductInCart = cart.items.some(
        item => item.productId === productId
      )
      if (isProductInCart) {
        updateCartItem({
          productId,
          quantity,
          isAssembled: isAssembled[productId],
          hingeDirection: hinges[productId]
        })
      }
    }
  }

  const handleAssembledChange = (productId: string) => {
    setIsAssembled(prev => {
      const updated = { ...prev, [productId]: !prev[productId] }
      localStorage.setItem('isAssembled', JSON.stringify(updated))
      return updated
    })

    const product = searchResults.find(p => p.id === productId)

    if (!isAssembled[productId]) {
      if (product?.isHingeable) {
        setHinges(prev => {
          const updated = { ...prev, [productId]: 'LEFT' }
          localStorage.setItem('hinges', JSON.stringify(updated))
          return updated
        })
      } else {
        setHinges(prev => {
          const updated = { ...prev, [productId]: '' }
          localStorage.setItem('hinges', JSON.stringify(updated))
          return updated
        })
      }
    } else {
      setHinges(prev => {
        const updated = { ...prev, [productId]: '' }
        localStorage.setItem('hinges', JSON.stringify(updated))
        return updated
      })
    }
  }

  const handleHingeChange = (productId: string, hinge: string) => {
    setHinges(prev => {
      const updated = { ...prev, [productId]: hinge }
      localStorage.setItem('hinges', JSON.stringify(updated))
      return updated
    })
  }

  const deleteSearchResult = (productId: string) => {
    const updatedResults = searchResults.filter(
      product => product && product.id !== productId
    )
    setSearchResults(updatedResults)
    localStorage.setItem('searchResults', JSON.stringify(updatedResults))

    const newQuantities = { ...quantities }
    delete newQuantities[productId]
    setQuantities(newQuantities)
    localStorage.setItem('quantities', JSON.stringify(newQuantities))

    const newIsAssembled = { ...isAssembled }
    delete newIsAssembled[productId]
    setIsAssembled(newIsAssembled)
    localStorage.setItem('isAssembled', JSON.stringify(newIsAssembled))

    const newHinges = { ...hinges }
    delete newHinges[productId]
    setHinges(newHinges)
    localStorage.setItem('hinges', JSON.stringify(newHinges))
  }

  const addAllToCart = async () => {
    await Promise.all(
      searchResults.map(product =>
        updateCartItem({
          productId: product.id,
          quantity: quantities[product.id] || 1,
          isAssembled: isAssembled[product.id] ?? false,
          hingeDirection: isAssembled[product.id] ? hinges[product.id] : ''
        })
      )
    )

    setSearchResults([])
    setQuantities({})
    setIsAssembled({})
    setHinges({})
    localStorage.clear()
  }

  useEffect(() => {
    const savedResults = localStorage.getItem('searchResults')
    if (savedResults) {
      setSearchResults(JSON.parse(savedResults))
    }

    const savedQuantities = localStorage.getItem('quantities')
    if (savedQuantities) {
      const parsedQuantities = JSON.parse(savedQuantities)
      const initializedQuantities = { ...parsedQuantities }
      setSearchResults(prev => {
        prev.forEach((product: Product) => {
          if (!initializedQuantities[product.id]) {
            initializedQuantities[product.id] = 1
          }
        })
        setQuantities(initializedQuantities)
        return prev
      })
    } else {
      setQuantities({})
    }

    const savedAssembled = localStorage.getItem('isAssembled')
    if (savedAssembled) {
      setIsAssembled(JSON.parse(savedAssembled))
    }

    const savedHinges = localStorage.getItem('hinges')
    if (savedHinges) {
      setHinges(JSON.parse(savedHinges))
    }
  }, [])

  return (
    <FixedWidthContainer sx={{ my: '50px' }}>
      <SectionHeader title={t('quickOrder.title')} />
      <QuickOrderSearchBar
        searchKeyword={searchKeyword}
        setSearchKeyword={setSearchKeyword}
        suggestions={suggestions}
        addProductToResults={addProductToResults}
        handleSearchChange={handleSearchChange}
        handleSuggestionSelect={handleSuggestionSelect}
      />

      <Box display='flex' alignItems='left' sx={{ my: 2 }}>
        <Typography variant='h4'>{t('quickOrder.productList')}</Typography>
      </Box>
      <Divider sx={{ width: '100%', mb: 2 }} />
      <QuickOrderList
        searchResults={searchResults}
        quantities={quantities}
        isAssembled={isAssembled}
        hinges={hinges}
        handleQuantityChange={handleQuantityChange}
        handleAssembledChange={handleAssembledChange}
        handleHingeChange={handleHingeChange}
        deleteSearchResult={deleteSearchResult}
        clearAll={() => {
          setSearchResults([])
          setQuantities({})
          setIsAssembled({})
          setHinges({})
          localStorage.clear()
        }}
        addAllToCart={addAllToCart}
      />
    </FixedWidthContainer>
  )
}

export default QuickOrderPage
